import { Box, Button, Checkbox, Divider, FormControlLabel, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

const TERMS_URL = 'https://terms.flowing.tel/';

export interface TermsAgreementProps {
    /** 필수 약관 전체 동의 여부 */
    requiredAgreed: boolean;
    onRequiredChange: (checked: boolean) => void;
    /** 마케팅 수신 동의 (선택) */
    marketingAgreed: boolean;
    onMarketingChange: (checked: boolean) => void;
}

/**
 * 약식 약관 동의 — 필수 1줄 + 선택 1줄.
 *
 * 이전에는 필수 항목 4개를 개별 체크박스로 펼쳐 받았지만, 전부 필수라 따로 받을 실익이 없고
 * 가입 마찰만 키웠다. 항목은 캡션으로 명시하고 전문은 "약관 보기"로 넘긴다.
 *
 * 새 i18n 키를 만들지 않기 위해 라벨은 기존 키를 조합한다.
 */
export default function TermsAgreement({
    requiredAgreed,
    onRequiredChange,
    marketingAgreed,
    onMarketingChange,
}: TermsAgreementProps) {
    const { t } = useTranslation();

    const requiredItems = [
        t('auth.serviceTerms'),
        t('auth.privacyPolicyAgreement'),
        t('auth.salesPolicyAgreement'),
        t('auth.ageVerification'),
    ].join(' · ');

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <FormControlLabel
                    sx={{ mr: 0, cursor: 'pointer' }}
                    control={<Checkbox sx={{ cursor: 'pointer' }} />}
                    checked={requiredAgreed}
                    onChange={(_, checked) => onRequiredChange(checked)}
                    label={
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                                {t('auth.agreeAll')}
                            </Typography>
                            <Typography component="span" variant="body2" color="error">
                                ({t('common.required')})
                            </Typography>
                        </Box>
                    }
                />
                <Button
                    size="small"
                    href={TERMS_URL}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ cursor: 'pointer', flexShrink: 0, color: 'text.secondary' }}
                >
                    {t('auth.viewAll')}
                </Button>
            </Box>

            <Typography
                variant="caption"
                sx={{ display: 'block', pl: 4, mt: -0.5, color: 'text.secondary', lineHeight: 1.6 }}
            >
                {requiredItems}
            </Typography>

            <Divider sx={{ my: 1.5 }} />

            <FormControlLabel
                sx={{ mr: 0, cursor: 'pointer' }}
                control={<Checkbox sx={{ cursor: 'pointer' }} />}
                checked={marketingAgreed}
                onChange={(_, checked) => onMarketingChange(checked)}
                label={
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography component="span" variant="body2">
                            {t('auth.marketingAgreement')}
                        </Typography>
                        <Typography component="span" variant="body2" color="text.secondary">
                            ({t('common.optional')})
                        </Typography>
                    </Box>
                }
            />
        </Box>
    );
}
